import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const REQUEST_TIMEOUT = 30000; // 30 seconds

export const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

export const axiosPrivateInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

// =============================================================================
// In-flight request deduplication
// =============================================================================
//
// Why this exists:
//   ProjectSummaryPage and FindContractorPage both mount the contractor-
//   search effect, and React StrictMode mounts each effect twice in dev.
//   The result is two identical POSTs firing within ~2ms of each other —
//   wasted backend work, doubled DB queries, occasional race in the
//   contractor list state. Per-page AbortControllers can't fix it
//   because each page only knows about its own pending requests.
//
//   Only public, read-only searches share an in-flight result. Private clients,
//   explicit authentication and independently cancellable calls stay separate.
//   Mutations (quotes, chat, uploads, payments) are always separate requests.

const COALESCE_PATHS = [
  "/profiles/contractors/search/cards/",
];

function _coalesceKey(config) {
  const method = (config.method || "get").toLowerCase();
  const url = config.url || "";
  // Normalise body: object -> stable JSON, undefined -> empty
  let bodyKey = "";
  if (config.data) {
    try {
      bodyKey = typeof config.data === "string"
        ? config.data
        : JSON.stringify(config.data);
    } catch {
      bodyKey = "<unserialisable>";
    }
  }
  return JSON.stringify([method, config.baseURL, url, bodyKey, config.params,
    config.timeout, config.responseType, config.withCredentials]);
}

function _isSimpleJson(value) {
  if (value == null || ["string", "boolean"].includes(typeof value)) return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (Object.getPrototypeOf(value) !== Object.prototype) return false;
  return Object.values(value).every(v => v == null || ["string", "boolean"].includes(typeof v) ||
    (typeof v === "number" && Number.isFinite(v)));
}

function _hasAuthHeader(headers) {
  return Object.entries(headers || {}).some(([key, value]) =>
    ["authorization", "x-csrftoken", "cookie"].includes(key.toLowerCase()) ||
    (value && typeof value === "object" && _hasAuthHeader(value)));
}

function _shouldCoalesce(config) {
  const url = config.url || "";
  // Axios supports URLSearchParams, custom serializers, auth and transforms.
  // Those are not interchangeable merely because JSON.stringify looks equal.
  if (config.signal || config.cancelToken || config.auth || config.paramsSerializer ||
      _hasAuthHeader(config.headers) || !_isSimpleJson(config.params) || !_isSimpleJson(config.data)) return false;
  return ["get", "post"].includes((config.method || "get").toLowerCase()) &&
    COALESCE_PATHS.some((p) => url.split("?")[0].endsWith(p));
}

function _attachCoalescing(instance) {
  const inFlight = new Map();
  const originalRequest = instance.request.bind(instance);
  instance.request = function (config) {
    if (config.headers || config.adapter || config.transformRequest || config.transformResponse) return originalRequest(config);
    const effectiveConfig = axios.mergeConfig(instance.defaults, config);
    if (!_shouldCoalesce(effectiveConfig)) return originalRequest(config);
    const key = _coalesceKey(effectiveConfig);
    const existing = inFlight.get(key);
    if (existing) {
      // Return the same Promise. Both callers see the same response.
      return existing;
    }
    const promise = originalRequest(config).finally(() => {
      inFlight.delete(key);
    });
    inFlight.set(key, promise);
    return promise;
  };
  // Axios binds helpers to its internal context; wrapping .request alone
  // does not affect .get/.post, which our pages normally call.
  instance.get = (url, config = {}) => instance.request({ ...config, method: "get", url });
  instance.post = (url, data, config = {}) => instance.request({ ...config, method: "post", url, data });
}

_attachCoalescing(axiosInstance);
